/* «Склад» — one menu section, four ways to look at one database (owner,
 * 2026-09-19: «в 3х разделах у нас одна база с продуктами и у всех разный
 * вид»). Under the section title a row of switches picks the view:
 *
 *   Склад    — the shelf, a batch at a time          /t/:slug/stock
 *   Продукты — the cards, every field               /t/:slug/products
 *   Цены     — «Формирование цен»                   /t/:slug/prices
 *   Доступ   — client prices and access             /t/:slug/prices?tab=clients
 *
 * Each view keeps its own address, so old links and bookmarks still open it.
 * Which of the four a member gets is decided by the same gates the sidebar
 * items they replaced used: the company's surfaces, the member's actions, and
 * the section permission. The sidebar shows the section while any view is
 * left, and opens the first one.
 */
import type { AuthState } from "./auth";
import { can, canAction, canNavigate, membershipFor } from "./permissions";
import { hasSurface } from "./surfaces";

export type CatalogView = "stock" | "products" | "prices" | "access";

export const CATALOG_VIEWS: CatalogView[] = ["stock", "products", "prices", "access"];

/** A member whose role sees only part of pricing gets the one plain price
 *  table (EmployeePrices), not the formation sheet and the client matrix. */
export function isEmployeePrices(state: AuthState, slug: string): boolean {
  const m = membershipFor(state, slug);
  return (
    m?.permission_version === 2 &&
    (!["owner", "manager"].includes(m?.role ?? "") ||
      !["purchase_prices.edit", "sales_prices.edit", "assortment.manage", "company_settings.edit"].every((a) =>
        canAction(state, slug, a),
      ))
  );
}

function hasPrices(state: AuthState, slug: string): boolean {
  return hasSurface(state, slug, "nav.prices") && canNavigate(state, slug, "prices") && can(state, slug, "pricing", "view");
}

export function hasCatalogView(state: AuthState, slug: string, view: CatalogView): boolean {
  switch (view) {
    case "stock":
      return hasSurface(state, slug, "nav.stock") && canNavigate(state, slug, "stock") && can(state, slug, "products", "view");
    case "products":
      return hasSurface(state, slug, "nav.products") && canNavigate(state, slug, "products") && can(state, slug, "products", "view");
    case "prices":
      // The employee table stands in for both price views, so it is listed once.
      return hasPrices(state, slug) && (isEmployeePrices(state, slug) || hasSurface(state, slug, "prices.formation"));
    case "access":
      return hasPrices(state, slug) && !isEmployeePrices(state, slug) && hasSurface(state, slug, "prices.distributors");
  }
}

export function catalogViews(state: AuthState, slug: string): CatalogView[] {
  return CATALOG_VIEWS.filter((v) => hasCatalogView(state, slug, v));
}

export function catalogViewPath(slug: string, view: CatalogView): string {
  switch (view) {
    case "stock": return `/t/${slug}/stock`;
    case "products": return `/t/${slug}/products`;
    case "prices": return `/t/${slug}/prices?tab=formation`;
    case "access": return `/t/${slug}/prices?tab=clients`;
  }
}
